import { useMemo, useState } from "react";
import {
  useGetFactoriesQuery,
  useDeleteFactoryMutation,
} from "../../../../redux/api";
import type { Factory } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { FactoryFormModal } from "./factory";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Factories / Suppliers (list page).
const Factories = () => {
  const {
    data: factories = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetFactoriesQuery();

  const [deleteFactory] = useDeleteFactoryMutation();

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = factories.find((f) => f.id === viewingId) ?? null;


  const handleDelete = async (factory: Factory) => {
    if (!window.confirm(`Delete "${factory.name}"?`)) return;
    try {
      await deleteFactory(factory.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete factory.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return factories;
    return factories.filter((f: Factory) =>
      [f.name, f.code, f.email, f.phone, f.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [factories, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load factories"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Factories"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Email", value: viewing.email },
              { label: "Phone", value: viewing.phone },
              { label: "GSTIN", value: viewing.gstin },
              { label: "Contact person", value: viewing.contactPerson },
              { label: "Address line 1", value: viewing.addressLine1, full: true },
              { label: "Address line 2", value: viewing.addressLine2, full: true },
              { label: "City", value: viewing.city },
              { label: "State", value: viewing.state },
              { label: "Country", value: viewing.country },
              { label: "Postal code", value: viewing.postalCode },
              { label: "Lead time (days)", value: viewing.leadTimeDays },
            ]}
          />
        </EntityOverview>
        <FactoryFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Factories</h2>
            <p className="panel__sub">
              Showing {visible.length} of {factories.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search factories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New factory
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading factories…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {factories.length === 0
                      ? "No factories yet."
                      : "No factories match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((factory: Factory) => (
                  <tr key={factory.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(factory.id)}
                      >
                        {factory.name}
                      </button>
                    </td>
                    <td>
                      {factory.code ? (
                        <span className="pill pill--neutral">{factory.code}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="cell-date">{factory.email ?? "—"}</td>
                    <td>{factory.phone ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(factory.status)}`}>
                        <span className="badge__dot" />
                        {factory.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(factory.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(factory.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(factory),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <FactoryFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Factories;
